import { Btn } from '../ui';
import { NextServiceInfo, PMStatus } from '../../types/pmTypes';
import { calculateRemainingUM, calculatePMStatus, getPrimaryServiceLevel } from '../../utils/pmUtils';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import '../../css/PMEquipmentConfiguration.css';

interface NextScheduledServiceCardProps {
    nextServiceInfo: NextServiceInfo | null;
    currentUM: number;
    unitOfMeasure: string;
    dueSoonPercentage: number;
    onPerformService: (serviceLevelIds: string[]) => void;
}

/**
 * Displays the next due service level summary card with service level info,
 * target UM, current UM, remaining UM, status, and a "Perform PM Service" button.
 * Supports cascading intervals by showing all due levels at the next service point.
 *
 * Validates: Requirements 1.4, 2.4, 3.1
 */
export const NextScheduledServiceCard = ({
    nextServiceInfo,
    currentUM,
    unitOfMeasure,
    dueSoonPercentage,
    onPerformService,
}: NextScheduledServiceCardProps) => {
    const umLabel = displayUnitOfMeasure(unitOfMeasure);

    if (!nextServiceInfo) {
        return (
            <div className="pm-next-service-card">
                <h3>Next Scheduled Service</h3>
                <p className="pm-config-empty">No service levels configured for this unit.</p>
            </div>
        );
    }

    const remaining = calculateRemainingUM(currentUM, nextServiceInfo.targetUM);
    const status = calculatePMStatus({
        currentUM,
        targetUM: nextServiceInfo.targetUM,
        dueSoonPercentage,
        intervalUM: getPrimaryServiceLevel(nextServiceInfo.dueLevels)?.intervalValue,
    });

    const getStatusBadgeClass = (s: PMStatus): string => {
        switch (s) {
            case PMStatus.OVERDUE: return 'overdue';
            case PMStatus.DUE_SOON: return 'due-soon';
            default: return 'scheduled';
        }
    };

    const dueLevelNames = nextServiceInfo.dueLevels
        .map(sl => sl.levelIdentifier)
        .join(', ');

    return (
        <div className="pm-next-service-card">
            <h3>Next Scheduled Service</h3>
            <div className="pm-next-service-details">
                <div className="pm-next-service-item">
                    <span className="label">Service Level</span>
                    <span className="value">{dueLevelNames}</span>
                </div>
                <div className="pm-next-service-item">
                    <span className="label">Target UM</span>
                    <span className="value">{nextServiceInfo.targetUM.toLocaleString()} {umLabel}</span>
                </div>
                <div className="pm-next-service-item">
                    <span className="label">Current UM</span>
                    <span className="value">{currentUM.toLocaleString()} {umLabel}</span>
                </div>
                <div className="pm-next-service-item">
                    <span className="label">Remaining</span>
                    <span className="value">{remaining.toLocaleString()} {umLabel}</span>
                </div>
                <div className="pm-next-service-item">
                    <span className="label">Status</span>
                    <span className={`pm-status-badge ${getStatusBadgeClass(status)}`}>
                        {status}
                    </span>
                </div>
            </div>
            <div className="pm-next-service-actions">
                <Btn
                    variant="primary"
                    buttonText="Perform PM Service"
                    buttonOnClick={() => onPerformService(nextServiceInfo.dueLevels.map((sl) => sl.id))}
                />
            </div>
        </div>
    );
};
